import { Button } from '../../components/Button';
import { PageHeading } from '../../components/PageHeading';
import { BillingDetailsForm } from '../../composites/BillingDetailsForm';
import { CheckoutSummaryMobile } from '../../composites/CheckoutSummaryMobile';
import { CheckoutSummaryPanel } from '../../composites/CheckoutSummaryPanel';
import { DeliveryStep } from '../../composites/DeliveryStep';
import { DepositPaymentPicker } from '../../composites/DepositPaymentPicker';
import { PaymentMethodForm } from '../../composites/PaymentMethodForm';
import { SignInModal } from '../../composites/SignInModal';
import {
  ExpressCheckoutBlock,
  ExpressCheckoutDivider,
  ExpressCheckoutLabel,
  MainColumn,
  MobileSummaryWrapper,
  PageGrid,
  PageWrapper,
  SidebarColumn,
  StepBlock,
  StepHeaderRow,
  StepNumber,
  StepTitle,
  SubmitButtonWrapper,
  SubmittingOverlay,
  TermsLine,
  TermsLink,
} from './CheckoutTemplate.styles';
import type { CheckoutTemplateProps } from './CheckoutTemplate.types';

export function CheckoutTemplate({
  requiresPayment,
  isSubmitting = false,
  submitDisabled = false,
  isLoggedIn = false,

  depositOptions,
  depositValue,
  onDepositChange,

  billingValues,
  onBillingChange,
  billingErrors,
  titleOptions,
  countryOptions,
  phoneCountryCodes,

  hasDeliverableItems = false,
  isPostalAllowed = true,
  isCollectionAllowed = true,
  canUseBillingAsShipping = true,
  deliveryCountryOptions,
  deliveryValues,
  onDeliveryMethodChange,
  onUseBillingAsShippingChange,
  onShippingChange,
  postalCost,
  collectionDetails,
  deliveryErrors,

  paymentSlot,
  acceptedCards,
  paymentError,
  paymentLabelsOverride,

  expressCheckoutSlot,

  onSubmit,

  signInOpen,
  onSignInOpenChange,
  signInValues,
  onSignInChange,
  onSignInSubmit,
  signInErrors,
  isSigningIn,
  onForgottenPasswordClick,

  signUpValues,
  onSignUpChange,
  onSignUpSubmit,
  isSigningUp,
  signUpErrors,

  basketItems,
  basketTotal,
  basketView,
  onBasketViewChange,
  basketGuests,
  onBasketRemoveItem,
  basketSubtotal,
  basketPromotionsDiscount,
  basketVouchersDiscount,
  basketDeliveryCost,
  promoCode,
  voucherCode,

  termsHref,
  onTermsClick,

  locale,
  labels,
}: CheckoutTemplateProps) {
  const showDepositStep = depositOptions.length > 0;

  const basketPanel = (
    <CheckoutSummaryPanel
      items={basketItems}
      totalPrice={basketTotal}
      view={basketView}
      onViewChange={onBasketViewChange}
      guests={basketGuests}
      onRemoveItem={onBasketRemoveItem}
      subtotal={basketSubtotal}
      promotionsDiscount={basketPromotionsDiscount}
      vouchersDiscount={basketVouchersDiscount}
      deliveryCost={basketDeliveryCost}
      promoCode={promoCode}
      voucherCode={voucherCode}
      locale={locale}
      labels={{
        itemisedView: labels.basketItemisedView,
        itineraryView: labels.basketItineraryView,
        addedConfirmation: labels.basketAddedConfirmation,
        subtotal: labels.basketSubtotal,
        promotions: labels.basketPromotions,
        vouchers: labels.basketVouchers,
        delivery: labels.basketDelivery,
        total: labels.basketTotal,
        removeLabel: labels.basketRemoveLabel,
        noImageLabel: labels.basketNoImageLabel,
        selectGuestLabel: labels.basketSelectGuestLabel,
      }}
    />
  );

  return (
    <>
      <PageWrapper data-testid="checkout-template">
        <PageHeading title={labels.pageTitle} subtitle={labels.pageSubtitle} />

        <MobileSummaryWrapper>
          <CheckoutSummaryMobile
            total={basketTotal}
            locale={locale}
            labels={{
              showSummary: labels.mobileShowSummary,
              hideSummary: labels.mobileHideSummary,
            }}
          >
            {basketPanel}
          </CheckoutSummaryMobile>
        </MobileSummaryWrapper>

        <PageGrid>
          <MainColumn>
            <SubmittingOverlay $active={isSubmitting}>
              {showDepositStep && (
                <StepBlock>
                  <StepTitle>
                    <StepNumber>{labels.whenToPayStepNumber}</StepNumber>
                    {labels.whenToPayTitle}
                  </StepTitle>
                  <DepositPaymentPicker
                    options={depositOptions}
                    value={depositValue}
                    onChange={onDepositChange}
                    labels={{ groupAriaLabel: labels.whenToPayTitle }}
                  />

                  {expressCheckoutSlot && (
                    <ExpressCheckoutBlock>
                      <ExpressCheckoutLabel>{labels.expressCheckoutLabel}</ExpressCheckoutLabel>
                      {expressCheckoutSlot}
                      <ExpressCheckoutDivider>
                        {labels.expressCheckoutDividerText}
                      </ExpressCheckoutDivider>
                    </ExpressCheckoutBlock>
                  )}
                </StepBlock>
              )}

              <StepBlock>
                <StepHeaderRow>
                  <StepTitle>
                    <StepNumber>{labels.detailsStepNumber}</StepNumber>
                    {labels.detailsTitle}
                  </StepTitle>
                  {!isLoggedIn && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      onClick={() => onSignInOpenChange(true)}
                    >
                      {labels.signInButton}
                    </Button>
                  )}
                </StepHeaderRow>
                <BillingDetailsForm
                  values={billingValues}
                  onChange={onBillingChange}
                  showCreateAccount={!isLoggedIn}
                  titleOptions={titleOptions}
                  countryOptions={countryOptions}
                  phoneCountryCodes={phoneCountryCodes}
                  showHeader={false}
                  errors={billingErrors}
                  labels={{
                    sectionTitle: labels.detailsTitle,
                    signInButton: labels.signInButton,
                    titleLabel: labels.billingTitleLabel,
                    titlePlaceholder: labels.billingTitlePlaceholder,
                    firstNameLabel: labels.billingFirstNameLabel,
                    lastNameLabel: labels.billingLastNameLabel,
                    emailLabel: labels.billingEmailLabel,
                    marketingOptInLabel: labels.billingMarketingOptInLabel,
                    addressLabel: labels.billingAddressLabel,
                    postalCodeLabel: labels.billingPostalCodeLabel,
                    countryLabel: labels.billingCountryLabel,
                    countryPlaceholder: labels.billingCountryPlaceholder,
                    phoneLabel: labels.billingPhoneLabel,
                    phoneSearchPlaceholder: labels.billingPhoneSearchPlaceholder,
                    createAccountLabel: labels.billingCreateAccountLabel,
                  }}
                />
              </StepBlock>

              {hasDeliverableItems &&
                deliveryValues &&
                onDeliveryMethodChange &&
                onUseBillingAsShippingChange &&
                onShippingChange && (
                  <StepBlock>
                    <StepTitle>
                      <StepNumber>{labels.deliveryStepNumber}</StepNumber>
                      {labels.deliveryTitle}
                    </StepTitle>
                    <DeliveryStep
                      values={deliveryValues}
                      onMethodChange={onDeliveryMethodChange}
                      onUseBillingAsShippingChange={onUseBillingAsShippingChange}
                      onShippingChange={onShippingChange}
                      isPostalAllowed={isPostalAllowed}
                      isCollectionAllowed={isCollectionAllowed}
                      canUseBillingAsShipping={canUseBillingAsShipping}
                      postalCost={postalCost}
                      collectionDetails={collectionDetails}
                      countryOptions={deliveryCountryOptions ?? countryOptions}
                      errors={deliveryErrors}
                      labels={{
                        chooseMethod: labels.deliveryChooseMethod,
                        methodPostal: labels.deliveryMethodPostal,
                        methodCollection: labels.deliveryMethodCollection,
                        useBillingAsShipping: labels.deliveryUseBillingAsShipping,
                        shippingAddressLabel: labels.deliveryShippingAddressLabel,
                        shippingPostalCodeLabel: labels.deliveryShippingPostalCodeLabel,
                        shippingCountryLabel: labels.deliveryShippingCountryLabel,
                        shippingCountryPlaceholder: labels.deliveryShippingCountryPlaceholder,
                      }}
                    />
                  </StepBlock>
                )}

              {requiresPayment && (
                <StepBlock>
                  <StepTitle>
                    <StepNumber>{labels.paymentStepNumber}</StepNumber>
                    {labels.paymentTitle}
                  </StepTitle>
                  <PaymentMethodForm
                    acceptedCards={acceptedCards}
                    showHeader={false}
                    formError={paymentError}
                    labels={{
                      sectionTitle: labels.paymentTitle,
                      acceptedCardsAriaLabel: labels.paymentAcceptedCardsAriaLabel,
                      ...paymentLabelsOverride,
                    }}
                  >
                    {paymentSlot}
                  </PaymentMethodForm>
                </StepBlock>
              )}

              <SubmitButtonWrapper>
                <Button
                  type="button"
                  variant="primary"
                  fullWidth
                  onClick={onSubmit}
                  disabled={isSubmitting || submitDisabled}
                >
                  {requiresPayment ? labels.payButton : labels.confirmButton}
                </Button>
                <TermsLine>
                  {labels.termsPrefix}{' '}
                  <TermsLink
                    href={termsHref}
                    onClick={onTermsClick}
                    target={termsHref ? '_blank' : undefined}
                    rel={termsHref ? 'noreferrer' : undefined}
                  >
                    {labels.termsLinkText}
                  </TermsLink>
                  {labels.termsSuffix}
                </TermsLine>
              </SubmitButtonWrapper>
            </SubmittingOverlay>
          </MainColumn>

          <SidebarColumn>{basketPanel}</SidebarColumn>
        </PageGrid>
      </PageWrapper>

      <SignInModal
        open={signInOpen}
        onClose={() => onSignInOpenChange(false)}
        values={signInValues}
        onChange={onSignInChange}
        onSubmit={onSignInSubmit}
        onForgottenPasswordClick={onForgottenPasswordClick}
        isSubmitting={isSigningIn}
        errors={signInErrors}
        signUpValues={signUpValues}
        onSignUpChange={onSignUpChange}
        onSignUpSubmit={onSignUpSubmit}
        isSigningUp={isSigningUp}
        signUpErrors={signUpErrors}
        labels={{
          title: labels.signInModalTitle,
          subtitle: labels.signInModalSubtitle,
          signUpPrompt: labels.signInModalSignUpPrompt,
          signUpLink: labels.signInModalSignUpLink,
          emailLabel: labels.signInModalEmailLabel,
          passwordLabel: labels.signInModalPasswordLabel,
          submit: labels.signInModalSubmit,
          forgottenPassword: labels.signInModalForgottenPassword,
          close: labels.signInModalClose,
          signUpTitle: labels.signUpModalTitle,
          signUpSubtitle: labels.signUpModalSubtitle,
          signInPrompt: labels.signUpModalSignInPrompt,
          signInLink: labels.signUpModalSignInLink,
          isSpaMember: labels.signUpModalIsSpaMember,
          spaMemberNumberLabel: labels.signUpModalSpaMemberNumberLabel,
          signUpSubmit: labels.signUpModalSubmit,
        }}
      />
    </>
  );
}
